import ChevronRight from '@material-ui/icons/ChevronRight';
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../constants/colors';
import { analyticsTrackEvent } from '../../../core/analytics';
import {
  ACTION_LOAD_SYMPTOMS_TILES,
  ACTION_SEE_SYMPTOMS_TILES,
  ACTION_VIEW_FEWER_SERVICES,
  ACTION_VIEW_MORE_SERVICES,
  HOMEPAGE,
} from '../../../core/analytics/events';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import Observer from '../../SolvPatternLibrary/Profilers/Observer';
import SectionTitle from '../SectionTitle';
import { Service, services } from './services';
import ServiceTag from './ServiceTag';
import { fontFamily } from '~/constants/text';

const Root = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-width: 850px;
  margin: 0 auto;

  .hidden {
    display: none;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: -5px;
  margin-top: 20px;

  > * {
    display: block;
    margin: 5px;
  }
`;

const ViewMoreButton = styled.button`
  font-family: ${fontFamily.book};

  appearance: none;
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  margin-top: 20px;
  color: ${colors.crunchBerry};
  display: flex;
  align-items: center;

  > *:last-child {
    margin-left: 5px;
  }
`;

type Props = {
  /** Callback to update the search bar */
  setSearchValues: (service: Service) => any;
};

/**
 * Section to show available services and quick searches.
 *
 */
const ServicesSection = ({ setSearchValues }: Props) => {
  const aboveMd = useBreakpoint('md');
  const [showMore, setShowMore] = useState(false);

  const numToShow = aboveMd ? 9 : 6;

  const variants = {
    visible: {
      y: 0,
      opacity: 1,
    },
    hidden: {
      y: 10,
      opacity: 0,
    },
  };

  useEffect(() => {
    analyticsTrackEvent(HOMEPAGE, {
      action: ACTION_LOAD_SYMPTOMS_TILES,
    });
  }, []);

  // Handle whether this is seen
  const [seenOnce, setSeenOnce] = useState(false);

  const handleObserverChange = () => {
    if (!seenOnce) {
      setSeenOnce(true);
      const action = ACTION_SEE_SYMPTOMS_TILES;
      analyticsTrackEvent(HOMEPAGE, { action });
    }
  };

  const handleViewMoreClick = () => {
    const action = showMore ? ACTION_VIEW_FEWER_SERVICES : ACTION_VIEW_MORE_SERVICES;
    analyticsTrackEvent(HOMEPAGE, { action });
    setShowMore(!showMore);
  };

  return (
    <Observer handleSawComponent={handleObserverChange}>
      <Root>
        <AnimateSharedLayout>
          <SectionTitle>What can we help with?</SectionTitle>
          <Tags>
            <AnimatePresence>
              {services
                .filter((s, i) => s.visible)
                .filter((_, i) => showMore || i < numToShow)
                .map((service) => (
                  <motion.div
                    animate="visible"
                    initial="hidden"
                    key={service.name}
                    variants={variants}
                  >
                    <ServiceTag onClick={() => setSearchValues(service)} title={service.name} />
                  </motion.div>
                ))}
            </AnimatePresence>
          </Tags>

          <motion.div layout>
            <ViewMoreButton onClick={handleViewMoreClick}>
              View {showMore ? 'Fewer' : 'More'}
              <motion.div animate={{ rotate: showMore ? -90 : 0 }}>
                <ChevronRight />
              </motion.div>
            </ViewMoreButton>
          </motion.div>
        </AnimateSharedLayout>
      </Root>
    </Observer>
  );
};

export default ServicesSection;
